import {useCallback} from "react"
import {router, usePage} from "@inertiajs/react"
import {useChannel} from "@/hooks/use-channel"
import {V2_NOTIFICATIONS_POPOVER_PROPS} from "@/lib/notifications-props"
import type {
  Availability,
  VoiceStatus,
} from "@/types/v2/employee-status"
import type {SharedAuthProps} from "@/types/v2/auth"
import type {NotificationRow} from "@/types/v2/notifications"

// Subscribes to the per-employee ActionCable stream
// (`employee:<id>`) and reconciles incoming events with
// Inertia's page store. Mounted ONCE at the layout level
// (Sidebar / MobileTopBar are viewport branches; if the
// subscription is nested inside either, the other viewport
// stops syncing — same CON-82 lesson the v2 inbox already
// learned).
//
// Events we care about:
//   - notification.created   → partial-reload popover list
//   - notification.read      → decrement unread badge
//   - notification.read_all  → zero unread badge
//   - availability.updated   → write employeeStatus.availability
//   - voice_status.updated   → write employeeStatus.voiceStatus
export function useEmployeeStatusChannel() {
  const {props} = usePage<SharedAuthProps>()
  const employeeId = props.auth?.employee?.id

  const onReceived = useCallback(
    (data: Record<string, unknown>) => {
      const type = data.type
      switch (type) {
        case "notification.created":
          router.reload({
            only: [...V2_NOTIFICATIONS_POPOVER_PROPS],
          })
          break
        case "notification.read":
          if (typeof data.unreadCount === "number") {
            const next = data.unreadCount
            router.replaceProp("navCounts", (prev: unknown) => {
              if (!prev || typeof prev !== "object") return prev
              return {
                ...(prev as Record<string, unknown>),
                unreadNotifications: next,
              }
            })
            // `counts` lives only on the /v2/notifications
            // page, but updating it here keeps the filter
            // chip counts honest when another tab marks a
            // row read while the page is open.
            router.replaceProp("counts", (prev: unknown) => {
              if (!prev || typeof prev !== "object") return prev
              return {
                ...(prev as Record<string, unknown>),
                unread: next,
              }
            })
          }
          if (typeof data.notificationId === "number") {
            const id = data.notificationId
            const update = (rows: NotificationRow[] | undefined) =>
              (rows ?? []).map((r) =>
                r.id === id ? {...r, read: true} : r,
              )
            router.replaceProp("popoverNotifications", update)
            router.replaceProp("notifications", update)
          }
          break
        case "notification.read_all":
          router.replaceProp("navCounts", (prev: unknown) => {
            if (!prev || typeof prev !== "object") return prev
            return {
              ...(prev as Record<string, unknown>),
              unreadNotifications: 0,
            }
          })
          router.replaceProp("counts", (prev: unknown) => {
            if (!prev || typeof prev !== "object") return prev
            return {
              ...(prev as Record<string, unknown>),
              unread: 0,
            }
          })
          router.replaceProp(
            "popoverNotifications",
            (rows: NotificationRow[] | undefined) =>
              (rows ?? []).map((r) => ({...r, read: true})),
          )
          router.replaceProp(
            "notifications",
            (rows: NotificationRow[] | undefined) =>
              (rows ?? []).map((r) => ({...r, read: true})),
          )
          break
        case "availability.updated":
          if (typeof data.availability === "string") {
            const next = data.availability as Availability
            router.replaceProp("employeeStatus", (prev: unknown) => {
              if (!prev || typeof prev !== "object") return prev
              return {
                ...(prev as Record<string, unknown>),
                availability: next,
              }
            })
          }
          break
        case "voice_status.updated":
          if (typeof data.voiceStatus === "string") {
            const next = data.voiceStatus as VoiceStatus
            router.replaceProp("employeeStatus", (prev: unknown) => {
              if (!prev || typeof prev !== "object") return prev
              return {
                ...(prev as Record<string, unknown>),
                voiceStatus: next,
              }
            })
          }
          break
        default:
          break
      }
    },
    [],
  )

  // `useChannel` opens a subscription on mount; the server
  // rejects unauthenticated connections, so it's safe to
  // call unconditionally even when `employeeId` is null.
  // Keying on `employeeId` ensures the subscription is
  // recreated if the signed-in employee changes
  // (impersonation flows etc).
  useChannel({
    channel: "V2::EmployeeChannel",
    params: {employee_id: employeeId},
    onReceived,
  })
}
